import { EMPTY_VIEW_ICONS, EmptyView } from '@shared/components/ui/EmptyView';
import { TotalResults } from '@shared/components/ui/TotalResults';
import { Button } from '@shared/components/ui/button';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@shared/components/ui/shadcn/table';
import { formatCurrencyAmount, useCurrency } from '@modules/Orders/shared/hooks/useCurrency';
import { getTransactionDescription } from '@modules/Wallets/Wallet/lib/walletTransactionDescription';
import { faClockRotateLeft, faFileInvoice } from '@fortawesome/pro-light-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { accountingSyncToolbarButtons } from '../../config/accountingSyncToolbarConfig';
import { getAccountingSyncStatusBadge } from '../../config/accountingSyncStatusConfig';
import { useWalletAccountingSyncInfiniteList } from '../../hooks';
import type { AccountingSyncStatus, WalletAccountingSyncTransaction } from '../../models';
import { AccountingEntryFormattedModal, AccountingSyncAttemptsModal, AccountingSyncBreadcrumb } from '../display';
import { getAccountingEntrySearchBlob, hasAccountingEntryPayload } from '../../lib/accountingEntryDisplay';
import { ErrorState, LoadingState } from '../feedback';
import {
  AccountingSyncFiltersSection,
  AccountingSyncStatusToolbar,
  BuildPendingAccountingButton,
  ExportAccountingSyncCsvButton,
} from '../interaction';

const matchesSearch = (row: WalletAccountingSyncTransaction, term: string) => {
  if (!term) return true;
  const normalized = term.toLowerCase();
  const entryJson = getAccountingEntrySearchBlob(row);
  const searchable = [
    row.id?.toString(),
    row.wallet_id?.toString(),
    row.team_id?.toString(),
    row.team_name,
    row.type,
    row.description,
    row.reference_type,
    row.reference_id?.toString(),
    row.holded_entry_id,
    row.accounting_last_sync_error,
    entryJson,
  ];
  return searchable.some((v) => v?.toString().toLowerCase().includes(normalized));
};

export const ListMain = () => {
  const { t } = useTranslation('accountingSyncModule');
  const { t: tWallet } = useTranslation('walletsModule');
  const currency = useCurrency();
  const [activeTab, setActiveTab] = useState('all');
  const [searchValue, setSearchValue] = useState('');
  const [entryModalRow, setEntryModalRow] = useState<WalletAccountingSyncTransaction | null>(null);
  const [attemptsModalId, setAttemptsModalId] = useState<number | null>(null);
  const [attemptsModalOpen, setAttemptsModalOpen] = useState(false);

  const apiStatus = useMemo(() => {
    const btn = accountingSyncToolbarButtons.find((b) => b.key === activeTab);
    return (btn?.status as AccountingSyncStatus | undefined) ?? undefined;
  }, [activeTab]);

  const exportStatusFilter = apiStatus ?? 'all';

  const listFilters = useMemo(
    () => ({
      ...(apiStatus ? { status: apiStatus } : {}),
    }),
    [apiStatus],
  );

  const { data, isError, error, refetch, isFetching, isFetchingNextPage, fetchNextPage, hasNextPage } =
    useWalletAccountingSyncInfiniteList(listFilters);

  const rows: WalletAccountingSyncTransaction[] = useMemo(
    () => data?.pages.flatMap((p) => p.data) ?? [],
    [data?.pages],
  );

  const total = data?.pages[0]?.meta?.total ?? rows.length;

  const filteredRows = useMemo(() => rows.filter((row) => matchesSearch(row, searchValue)), [rows, searchValue]);

  const hasActiveFilters = Boolean(searchValue?.trim());
  const isBackgroundFetching = isFetching && !isFetchingNextPage;
  const emptyValueLabel = tWallet('table.emptyValue');

  const handleSetActiveTab = useCallback((key: string) => {
    setActiveTab(key);
  }, []);

  const handleScroll = useCallback(
    (e: React.UIEvent<HTMLDivElement>) => {
      const el = e.currentTarget;
      const threshold = 140;
      if (el.scrollHeight - el.scrollTop - el.clientHeight < threshold) {
        if (hasNextPage && !isFetchingNextPage) {
          void fetchNextPage();
        }
      }
    },
    [hasNextPage, isFetchingNextPage, fetchNextPage],
  );

  const openAttemptsModal = useCallback((id: number) => {
    setAttemptsModalId(id);
    setAttemptsModalOpen(true);
  }, []);

  if (isError) {
    return (
      <>
        <AccountingSyncBreadcrumb />
        <ErrorState error={error ?? new Error(t('errors.generic'))} onRetry={refetch} title={t('errors.title')} />
      </>
    );
  }

  if (!data) {
    return (
      <>
        <AccountingSyncBreadcrumb />
        <LoadingState message={t('loading.list')} />
      </>
    );
  }

  return (
    <>
      <AccountingSyncBreadcrumb />
      <div className="px-6 pt-4">
        <div className="shrink-0 absolute right-4 top-2 flex items-center gap-2">
          <BuildPendingAccountingButton />
        </div>

        <div className="mb-4">
          <div className="flex items-center justify-between flex-wrap gap-3 mb-4">
            <div className="flex items-center gap-3 flex-wrap">
              <AccountingSyncFiltersSection searchValue={searchValue} onSearch={setSearchValue} />
              <ExportAccountingSyncCsvButton statusFilter={exportStatusFilter} />
            </div>

            <div className="flex flex-col items-end ml-auto">
              <TotalResults total={total} />
            </div>
          </div>

          <AccountingSyncStatusToolbar active={activeTab} setActive={handleSetActiveTab} />
        </div>

        {filteredRows.length === 0 ? (
          <EmptyView
            type={hasActiveFilters ? 'noResults' : 'emptyState'}
            icon={EMPTY_VIEW_ICONS.ORDERS}
            title={hasActiveFilters ? t('empty.search_title') : t('empty.title')}
            description={hasActiveFilters ? t('empty.search_description') : t('empty.description')}
          />
        ) : (
          <div
            className={`transition-opacity duration-300 ${
              isBackgroundFetching ? 'opacity-50 pointer-events-none' : 'opacity-100'
            }`}
          >
            <div
              className="max-h-[calc(100vh-300px)] overflow-auto rounded-lg border border-card-border bg-card-bg"
              onScroll={handleScroll}
            >
              <Table>
                <TableHeader>
                  <TableRow className="border-card-border hover:bg-transparent bg-card-bg sticky top-0 z-10 shadow-sm">
                    <TableHead className="text-foreground bg-card-bg">{t('table.id')}</TableHead>
                    <TableHead className="text-foreground bg-card-bg">{t('table.team_name')}</TableHead>
                    <TableHead className="text-foreground bg-card-bg">{t('table.type')}</TableHead>
                    <TableHead className="text-foreground bg-card-bg min-w-[200px] max-w-[360px]">
                      {t('table.description')}
                    </TableHead>
                    <TableHead className="text-foreground bg-card-bg">{t('table.amount')}</TableHead>
                    <TableHead className="text-foreground bg-card-bg">{t('table.attempts')}</TableHead>
                    <TableHead className="text-foreground bg-card-bg">{t('table.status')}</TableHead>
                    <TableHead className="text-foreground bg-card-bg">{t('table.entry')}</TableHead>
                    <TableHead className="text-foreground bg-card-bg">{t('table.history')}</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredRows.map((row) => {
                    const desc = getTransactionDescription(
                      {
                        type: row.type,
                        description: row.description,
                        reference: { type: row.reference_type, id: row.reference_id },
                        related: row.related ?? null,
                      },
                      emptyValueLabel,
                    );
                    const teamLabel =
                      row.team_name?.trim() ||
                      (row.team_id != null ? `${t('table.team_fallback_prefix')} ${row.team_id}` : '—');

                    return (
                      <TableRow key={row.id} className="border-card-border">
                        <TableCell className="font-mono text-xs">{row.id}</TableCell>
                        <TableCell className="max-w-[180px] truncate">
                          <Link
                            to={`/wallets/${row.wallet_id}`}
                            className="text-primary hover:underline text-sm"
                            title={teamLabel}
                          >
                            {teamLabel}
                          </Link>
                        </TableCell>
                        <TableCell className="max-w-[120px] truncate" title={row.type}>
                          {row.type}
                        </TableCell>
                        <TableCell className="max-w-[360px] text-sm">
                          {desc.link ? (
                            <Link to={desc.link} className="text-primary hover:underline transition-colors">
                              {desc.text}
                            </Link>
                          ) : (
                            <span className="text-foreground">{desc.text}</span>
                          )}
                        </TableCell>
                        <TableCell>{formatCurrencyAmount(row.amount, currency)}</TableCell>
                        <TableCell>{row.accounting_sync_attempt_count}</TableCell>
                        <TableCell>{getAccountingSyncStatusBadge(row.accounting_sync_status, t)}</TableCell>
                        <TableCell className="align-middle">
                          {hasAccountingEntryPayload(row) ? (
                            <Button
                              type="button"
                              variant="outline"
                              size="sm"
                              className="h-8 gap-2 text-xs border-card-border bg-card-bg hover:bg-hover-press"
                              onClick={() => setEntryModalRow(row)}
                            >
                              <FontAwesomeIcon icon={faFileInvoice} className="w-3.5 h-3.5" />
                              {t('table.view_entry')}
                            </Button>
                          ) : (
                            <span className="text-muted-foreground text-xs">—</span>
                          )}
                        </TableCell>
                        <TableCell className="align-middle">
                          <Button
                            type="button"
                            variant="outline"
                            size="sm"
                            className="h-8 gap-2 text-xs border-card-border bg-card-bg hover:bg-hover-press"
                            onClick={() => openAttemptsModal(row.id)}
                            aria-label={t('table.open_history')}
                          >
                            <FontAwesomeIcon icon={faClockRotateLeft} className="w-3.5 h-3.5" />
                            {t('table.history')}
                          </Button>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
              {isFetchingNextPage && (
                <div className="py-3 text-center text-xs text-muted-foreground border-t border-card-border">
                  {t('infinite.loading_more')}
                </div>
              )}
            </div>
          </div>
        )}
      </div>

      <AccountingEntryFormattedModal
        open={entryModalRow !== null}
        onOpenChange={(open) => {
          if (!open) setEntryModalRow(null);
        }}
        source={entryModalRow}
      />

      <AccountingSyncAttemptsModal
        open={attemptsModalOpen}
        onOpenChange={(open) => {
          setAttemptsModalOpen(open);
          if (!open) setAttemptsModalId(null);
        }}
        transactionId={attemptsModalId}
      />
    </>
  );
};
